import { ArrowRight, Mail, Phone } from "lucide-react";
import { useLanguage } from "../../i18n/LanguageContext";

const CONTACT_EMAIL = "pan.martin.kuchar@gmail.com";
const CONTACT_PHONE_TEL = "+420602461799";
const PORTRAIT =
  "https://customer-assets.emergentagent.com/job_impact-platform-demo/artifacts/5men8oax_Martin%20Kucha%C5%99.png";

export const FinalCTA = () => {
  const { t } = useLanguage();
  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="contact"
      data-testid="final-cta-section"
      className="relative bg-[#FFE21A] text-[#181B24] py-16 md:py-20 overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <div className="lg:col-span-7">
            <h2
              data-testid="final-cta-headline"
              className="font-heading uppercase font-bold leading-[1.05] tracking-[0.01em] text-5xl sm:text-6xl md:text-7xl lg:text-8xl text-[#181B24]"
            >
              <span className="block mb-3">{t.cta.headline1}</span>
              <span className="block mb-3">
                <span className="inline-block bg-[#181B24] text-[#FFE21A] px-4 py-1.5 rounded-md shadow-[0_8px_24px_rgba(24,27,36,0.25)]">
                  {t.cta.headline2}
                </span>
              </span>
              <span className="block">
                {t.cta.headline3a}{" "}
                <span className="inline-block bg-[#181B24] text-[#FFE21A] px-4 py-1.5 rounded-md ml-2 shadow-[0_8px_24px_rgba(24,27,36,0.25)]">
                  {t.cta.headline3b}
                </span>
              </span>
            </h2>
            <p
              data-testid="final-cta-paragraph"
              className="mt-8 text-[#181B24]/80 font-body text-base md:text-lg leading-relaxed max-w-xl"
            >
              {t.cta.paragraph}
            </p>
          </div>

          <div className="lg:col-span-5 flex flex-col gap-5">
            <div className="relative mb-2" data-testid="cta-portrait-wrapper">
              <div className="relative w-full max-w-md mx-auto aspect-square rounded-[2rem] overflow-hidden shadow-[0_25px_60px_rgba(24,27,36,0.25)] ring-1 ring-[#181B24]/15">
                <img
                  src={PORTRAIT}
                  alt="Martin Kuchař — IT IMPACT"
                  className="w-full h-full object-cover"
                  style={{ objectPosition: "center 30%" }}
                  loading="lazy"
                  data-testid="cta-portrait-image"
                />
                <div className="absolute inset-x-0 bottom-0 h-1/4 bg-gradient-to-t from-[#181B24]/25 to-transparent pointer-events-none" />
                <div className="absolute top-4 right-4 w-3 h-3 rounded-full bg-[#FFE21A] ring-2 ring-white/60" />
              </div>

              <svg
                className="absolute -top-6 -left-6 md:-top-8 md:-left-8 opacity-60 pointer-events-none"
                width="160"
                height="160"
                viewBox="0 0 160 160"
                aria-hidden="true"
              >
                {Array.from({ length: 9 }).map((_, r) =>
                  Array.from({ length: 9 }).map((_, c) => (
                    <circle
                      key={`${r}-${c}`}
                      cx={6 + c * 18}
                      cy={6 + r * 18}
                      r="2.5"
                      fill="#181B24"
                    />
                  )),
                )}
              </svg>
              <svg
                className="absolute -bottom-4 -right-4 md:-bottom-6 md:-right-6 opacity-50 pointer-events-none"
                width="110"
                height="110"
                viewBox="0 0 110 110"
                aria-hidden="true"
              >
                {Array.from({ length: 6 }).map((_, r) =>
                  Array.from({ length: 6 }).map((_, c) => (
                    <circle
                      key={`${r}-${c}`}
                      cx={5 + c * 18}
                      cy={5 + r * 18}
                      r="2.5"
                      fill="#181B24"
                    />
                  )),
                )}
              </svg>
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-[#181B24] text-white rounded-full px-5 py-2 flex items-center gap-2 shadow-lg">
                <span className="w-1.5 h-1.5 rounded-full bg-[#FFE21A]" />
                <span className="font-heading font-bold uppercase tracking-[0.15em] text-xs md:text-sm">
                  Martin Kuchař
                </span>
              </div>
            </div>

            <a
              data-testid="cta-phone"
              href={`tel:${CONTACT_PHONE_TEL}`}
              className="group inline-flex items-center justify-between gap-4 rounded-full bg-[#181B24] text-[#FFE21A] font-heading font-bold uppercase tracking-[0.18em] text-sm md:text-base px-8 py-5 hover-lift shadow-[0_15px_40px_rgba(24,27,36,0.25)]"
            >
              <span className="flex items-center gap-3">
                <Phone size={20} strokeWidth={2.5} />
                {t.cta.phone}
              </span>
              <ArrowRight
                size={20}
                className="group-hover:translate-x-1 transition-transform"
              />
            </a>

            <button
              data-testid="cta-schedule-call"
              onClick={() => scrollTo("contact-details")}
              className="group inline-flex items-center justify-between gap-4 rounded-full bg-white text-[#181B24] font-heading font-bold uppercase tracking-[0.18em] text-sm md:text-base px-8 py-5 hover-lift shadow-[0_15px_40px_rgba(24,27,36,0.15)]"
            >
              {t.cta.schedule}
              <ArrowRight
                size={20}
                className="group-hover:translate-x-1 transition-transform"
              />
            </button>
            <a
              data-testid="cta-leave-message"
              href={`mailto:${CONTACT_EMAIL}?subject=IT%20IMPACT%20-%20Let's%20talk`}
              className="group inline-flex items-center justify-between gap-4 rounded-full bg-white text-[#181B24] font-heading font-bold uppercase tracking-[0.18em] text-sm md:text-base px-8 py-5 hover-lift shadow-[0_15px_40px_rgba(24,27,36,0.15)]"
            >
              {t.cta.leave}
              <Mail
                size={20}
                className="group-hover:translate-x-1 transition-transform"
              />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FinalCTA;
